
































// vue
import Vue from 'vue';
import NOC from '@/class/common/const';

// api
import API from '@/api/common';
import * as models from '@/class/models/views/setting/users';
import * as workstyleModels from '@/class/models/views/setting/workstyle';

// import util from '@/components/util';
import SideBarBase from '@/components/util/SideBarBase.vue';

import LaborCalendar from '@/parts/labor/LaborCalendar.vue';

import { ICustomHolidayView } from '@/class/models/typed/custom-holiday';

import { ILaborCalendarEvent } from '@/class/models/typed/labor';

import { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';

import { formatYmd, formatYm, addDays } from '@/class/common/util/date_util';

import { CalEvent } from '@/class/models/views/labor';
import { toHolidayEvents, toCalEvents } from '@/class/common/util/labor_calendar';

// util
import { GetUsingDepartmentsList, loadValidSideBarInfo, saveSideBarInfo, SideBarInfo } from '@/util/labor-management-util';

function resolveBaseMonth(queryMonth: unknown): { baseMonth: Date; shouldReplace: boolean } {
  if (typeof queryMonth === 'string') {
    const parts = queryMonth.split('-');
    const [y, m] = parts.map(Number);

    if (y && m && m >= 1 && m <= 12) {
      const baseMonth = new Date(y, m - 1, 1);
      const normalized = `${y}-${String(m).padStart(2, '0')}`;

      return {
        baseMonth,
        // yyyy-mm 以外（例: yyyy-mm-dd）は replace
        shouldReplace: parts.length !== 2 || queryMonth !== normalized,
      };
    }
  }

  return {
    baseMonth: new Date(),
    shouldReplace: true,
  };
}

const api = new API();

interface Data {
  currentRangeStart: Date | undefined;
  currentRangeEndExclusive: Date | undefined;
  baseEvents: CalEvent[];
  customHolidays: ICustomHolidayView[];
  departments: models.IDepartmentInfo[];
  usersList: models.IUser[];
  isDefault: boolean;
  selectedDepartmentID: number;
  selectedUserID: number;
  selectDepartment: models.IDepartmentInfo;
  selectUser: models.IUser;
  initialViewDate: Date;
  enableFixAccordion: boolean;
  searchText: string;
}
interface Methods {
  getParentID(v: any): number;
  getParentText(v: any): string;
  getChildID(v: any): number;
  getChildText(v: any): string;
  getChildTextSearch(v: any): string;
  SELECTED_ID(): string;
  onClickDepartment(department: models.IDepartmentInfo): void;
  onClickUser(user: models.IUser): Promise<void>;
  onRangeChange(payload: { start: Date; endExclusive: Date; month?: any }): Promise<void>;
  onDayClick(date: Date): void;
  loadDailyReports(start: Date, endExclusive: Date, month?: any): Promise<void>;
  fetchLaborEvents(start: Date, endExclusive: Date): Promise<void>;
  fetchCustomHolidays(): Promise<void>;
  inputSearchText(text: string): void;
}
interface Computed {
  sideBarInfo: SideBarInfo;
  combinedEvents: CalEvent[];
}

export default Vue.extend({
  name: 'LaborManagementMonthly',
  components: { SideBarBase, LaborCalendar },
  data() {
    return {
      baseEvents: [] as CalEvent[],
      customHolidays: [] as ICustomHolidayView[],
      currentRangeStart: undefined as Date | undefined,
      currentRangeEndExclusive: undefined as Date | undefined,
      departments: [] as models.IDepartmentInfo[],
      usersList: [] as models.IUser[], // 全従業員の詳細情報
      isDefault: false, // SP初期表示時はdefault:true
      selectedDepartmentID: -1,
      selectedUserID: -1,
      selectDepartment: {} as models.IDepartmentInfo,
      selectUser: {} as models.IUser,
      initialViewDate: new Date(),
      enableFixAccordion: true,
      searchText: '',
    };
  },
  created() {
    const { baseMonth } = resolveBaseMonth(this.$route.query.month);
    this.initialViewDate = baseMonth;
  },
  async mounted() {
    this.$emit('pushPath', this.$route.path);
    const query = this.$route.query;
    const res = await api.get<models.VUsers>('users');
    if (!res.result) {
      this.$emit('errorModal', 'エラーが発生しました。');
      return;
    }
    // -------------------------
    // データ初期化
    // -------------------------
    const { searchText } = this.$route.params;
    this.searchText = searchText || '';

    this.usersList = res.data.users;
    this.departments = GetUsingDepartmentsList(this.usersList, res.data.departments);

    const sideBarInfo = loadValidSideBarInfo(this.usersList, this.departments);
    this.departments = sideBarInfo.departments;
    this.selectedDepartmentID = sideBarInfo.selectedDepartmentID;
    this.selectedUserID = sideBarInfo.selectedUserID;

    if (this.selectedUserID !== -1) {
      const initialUser = this.usersList.find(v => v.userID === this.selectedUserID);
      await this.onClickUser(initialUser);
    }

    // -------------------------
    // SP用サイドバー初期表示
    // -------------------------
    if (query && query.md) {
      this.isDefault = true;
    }

    this.$nextTick(() => {
      this.enableFixAccordion = false;
    });
  },

  computed: {
    sideBarInfo(): SideBarInfo {
      const sideBarInfo = {
        departments: this.departments,
        selectedDepartmentID: this.selectedDepartmentID,
        selectedUserID: this.selectedUserID,
      };
      return sideBarInfo;
    },
    combinedEvents() {
      return [...this.baseEvents, ...toHolidayEvents(this.customHolidays)];
    },
  },
  watch: {
    departments: {
      deep: true,
      handler() {
        saveSideBarInfo(this.sideBarInfo);
      },
    },
  },
  methods: {
    getParentID(v: any) {
      return v.departmentID;
    },
    getParentText(v: any) {
      return v.name;
    },
    getChildID(v: any) {
      return v.userID;
    },
    getChildText(v: any) {
      return `${v.sei}${v.mei}`;
    },
    getChildTextSearch(v: any) {
      return `${v.sei}${v.mei}${v.seiKana}${v.meiKana}`;
    },
    SELECTED_ID(): string {
      return this.selectedDepartmentID > -1 ? `p${this.selectedDepartmentID}` : `c${this.selectedUserID}`;
    },
    onClickDepartment(department: models.IDepartmentInfo) {
      this.isDefault = false;
      this.selectedDepartmentID = department.departmentID;
      this.selectedUserID = -1;
      this.selectDepartment = department;

      const dep = this.departments.find(v => v.departmentID === department.departmentID);
      if (dep) {
        dep.isClosed = !dep.isClosed;
      }
    },
    async onClickUser(user: models.IUser) {
      this.isDefault = false;
      this.selectedDepartmentID = -1;
      this.selectedUserID = user.userID;
      this.selectUser = user;

      saveSideBarInfo(this.sideBarInfo);
      await this.loadDailyReports(this.currentRangeStart, this.currentRangeEndExclusive, undefined);
    },
    inputSearchText(text: string) {
      this.searchText = text;
    },

    async onRangeChange({ start, endExclusive, month }) {
      this.currentRangeStart = start;
      this.currentRangeEndExclusive = endExclusive;
      await this.loadDailyReports(start, endExclusive, month);
    },
    onDayClick(date: Date) {
      const selectedDate = formatYmd(date);
      this.$router.push({
        name: 'ManagementDaily',
        query: {
          date: selectedDate,
          user_id: String(this.selectedUserID),
        },
        params: {
          searchText: String(this.searchText),
        },
      });
    },
    async loadDailyReports(start: Date, endExclusive: Date, month: any) {
      if (!start || !endExclusive || this.selectedUserID <= 0) {
        return;
      }
      await Promise.all([this.fetchLaborEvents(start, endExclusive), this.fetchCustomHolidays()]);
      const { shouldReplace } = resolveBaseMonth(this.$route.query.month);

      if (month === undefined) {
        month = new Date(this.$route.query.month as string);
      }
      const formattedMonth = formatYm(month);

      const needReplace =
        shouldReplace ||
        String(this.$route.query.user_id) !== String(this.selectedUserID) ||
        String(this.$route.query.month) !== formattedMonth;

      if (needReplace) {
        this.$router.replace({
          name: this.$route.name!,
          query: {
            user_id: String(this.selectedUserID),
            month: formattedMonth,
          },
          params: {
            searchText: String(this.searchText),
          },
        });
      }
    },
    async fetchLaborEvents(start: Date, endExclusive: Date) {
      const from = formatYmd(start);
      const toExclusive = formatYmd(endExclusive);

      const res = await api.get<ILaborCalendarEvent[]>('labor/daily_reports', {
        userId: this.selectedUserID,
        periodStart: from,
        periodEndExclusive: toExclusive,
      });

      if (res.result && res.data) {
        this.baseEvents = toCalEvents(res.data);
      } else {
        this.baseEvents = [];
      }
    },
    async fetchCustomHolidays() {
      const resHoriday = await api.get<workstyleModels.VCustomHolidayList>('workstyle/customholiday');
      const holidays =
        resHoriday.data && Array.isArray(resHoriday.data.customHolidays) ? resHoriday.data.customHolidays : [];
      this.customHolidays = holidays;
    },
  },
} as ThisTypedComponentOptionsWithRecordProps<Vue, Data, Methods, Computed, {}>);
